import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Line, Bar } from "react-chartjs-2";
import FETCH from "../../../utils/fetch";
import assetHomeIcon from "../../../assets/images/asset.png";
import { ChartColors } from "../../../components/ChartColors";
import DASHBOARD_API from "../../../apis/dashboard.api";
import getFAClientName from "../../../helpers/getFAClientName";
import { exportElementToPaginatedPdf } from "../../../utils/pdfExport";
import { AssetsPDFReport } from "./AssetsPDFReport";
import DEATH_API from "../../../apis/death.api";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import "../AssetsDashboard.css";

export const AssetsReport = React.forwardRef((props, ref) => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  React.useImperativeHandle(ref, () => ({
    downloadPDF: () => {
      downloadPDF();
    },
    isDownloading: downloadLoading,
  }));
  const [assetsList, setAssetsList] = useState([]);
  const [inventoryList, setInventoryList] = useState([]);
  const [collectiblesList, setCollectiblesList] = useState([]);
  const [assetsName, setAssetsName] = useState([]);
  const [assetsValues, setAssetsValues] = useState([]);
  const [monetaryValue, setMonetaryValue] = useState(0);
  const [monetaryValueCollection, setMonetaryValueCollection] = useState(0);
  const [monetaryValueInventory, setMonetaryValueInventory] = useState(0);
  const [clientName, setClientName] = useState("");
  const [pdfChartImage, setPdfChartImage] = useState(null);
  const chartRef = useRef(null);

  // Dedicated PDF lists to prevent live UI pollution
  const [pdfAssetsList, setPdfAssetsList] = useState([]);
  const [pdfInventoryList, setPdfInventoryList] = useState([]);
  const [pdfCollectiblesList, setPdfCollectiblesList] = useState([]);

  const totalAssetsValue = (
    +monetaryValue +
    +monetaryValueCollection +
    +monetaryValueInventory
  ).toFixed(2);

  const getAssetsList = async () => {
    const userRole =
      localStorage.getItem("role") != "undefined" &&
      JSON.parse(localStorage.getItem("role"));
    const ID = localStorage.getItem("accessId");
    const datares = await FETCH.post({
      url: "client-module-service/client-module/list",
      id:
        userRole === "Financial Advisor Client"
          ? `?module=Asset&page=1&limit=10&clientId=${ID}`
          : `?module=Asset&page=1&limit=10`,
      body: {},
    });

    return datares && datares.records ? datares.records : [];
  };

  const getInventoryList = async () => {
    const userRole =
      localStorage.getItem("role") != "undefined" &&
      JSON.parse(localStorage.getItem("role"));
    const ID = localStorage.getItem("accessId");
    const datares = await FETCH.post({
      url: "client-module-service/client-module/list",
      id:
        userRole === "Financial Advisor Client"
          ? `?module=PROPERTY&page=1&limit=10&clientId=${ID}`
          : `?module=PROPERTY&page=1&limit=10`,
      body: {},
    });

    if (!(datares && datares.records)) return [];

    return datares.records.map((item) => ({
      ...item,
      "Name of Asset": item.name,
      "Account Type/Titled": item.module,
      "Held Where": item.city,
      "Monetary Value": item.total,
    }));
  };

  const getCollectiblesList = async () => {
    const datares = await DEATH_API.getCollectableValues();
    if (!(datares && datares.message)) return [];

    return datares.message.map((item, idx) => ({
      module: "Collectible",
      "Name of Asset": item.sub_type,
      "Account Type/Titled": "Collectible",
      "Held Where": null,
      "Monetary Value": item.monetary,
      "Value as of Date": null,
      id: idx + 1,
    }));
  };

  useEffect(() => {
    const names = [];
    const values = [];

    const processItems = (items) => {
      if (items && items.length > 0) {
        items.forEach((item) => {
          names.push(item["Name of Asset"] || "Unknown");
          
          const monetary = parseFloat(item["Monetary Value"] || 0);
          const cash = parseFloat(item["Cash Holding"] || 0);
          const stock = parseFloat(item["Stock Value"] || 0);
          
          let trueValue = monetary;
          if (item.module === "Investment Portfolio") {
            if (item.managedBy === "Plaid") {
              trueValue = monetary;
            } else {
              trueValue = cash + stock;
            }
          }
          values.push(trueValue);
        });
      }
    };

    processItems(assetsList);
    processItems(inventoryList);
    processItems(collectiblesList);

    setAssetsName(names);
    setAssetsValues(values);
  }, [assetsList, inventoryList, collectiblesList]);

  const fetchAllModuleDataForPDF = async (moduleName) => {
    let allData = [];
    let page = 1;
    let hasMore = true;
    const limit = 1000; // Superfast data fetch for PDFs
    const userRole =
      localStorage.getItem("role") != "undefined" &&
      JSON.parse(localStorage.getItem("role"));
    const ID = localStorage.getItem("accessId");

    while (hasMore) {
      let datares = await FETCH.post({
        url: "client-module-service/client-module/list",
        id:
          userRole === "Financial Advisor Client"
            ? `?module=${moduleName}&page=${page}&limit=${limit}&clientId=${ID}`
            : `?module=${moduleName}&page=${page}&limit=${limit}`,
        body: {},
      });

      if (datares && datares.records && datares.records.length > 0) {
        allData = [...allData, ...datares.records];
        if (datares.records.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }
    return allData;
  };

  const downloadPDF = async () => {
    try {
      setDownloadLoading(true);
      if (props.onDownloadLoading) props.onDownloadLoading(true);

      // 1. CAPTURE CHART FIRST (so it perfectly matches the live beautiful UI limited list)
      let chartImage = null;
      let chartInstance = null;

      // Poll up to 10 times (2 seconds) for the chart to be fully rendered 
      for (let i = 0; i < 10; i++) {
        chartInstance = (chartRef.current && chartRef.current.chartInstance) || 
                        (chartRef.current && typeof chartRef.current.getChart === 'function' ? chartRef.current.getChart() : null);
        
        if (chartInstance) {
          chartImage = chartInstance.toBase64Image();
          // Fallback: If Base64 string is too short or blank, try grabbing canvas directly
          if (!chartImage || chartImage.length < 50) {
            const canvasElement = document.querySelector('.assetsInfoMaincard canvas');
            if (canvasElement) chartImage = canvasElement.toDataURL("image/png");
          }
        }

        if (chartImage && chartImage.length > 50) {
          break; // Successfully captured a real image, break the loop!
        }
        
        console.log("Chart not fully ready, waiting 200ms...");
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      if (chartImage) {
        setPdfChartImage(chartImage);
      } else {
        console.error("Failed to capture chart after multiple attempts.");
      }

      // 2. Fetch Mass Data for the Tables underneath
      const allAssetsPromise = fetchAllModuleDataForPDF("Asset");
      const allInventoryPromise = fetchAllModuleDataForPDF("PROPERTY");
      const collectiblesPromise = DEATH_API.getCollectableValues().catch(e => {
        console.log("Error fetching collectibles", e); return { message: [] }; 
      });

      const [allAssets, allInventoryRaw, collectiblesRes] = await Promise.all([
        allAssetsPromise,
        allInventoryPromise,
        collectiblesPromise
      ]);

      const allInventory = allInventoryRaw.map((item) => ({
        ...item,
        "Name of Asset": item.name,
        "Account Type/Titled": item.module,
        "Held Where": item.city,
        "Monetary Value": item.total,
      }));

      let allCollectibles = [];
      if (collectiblesRes && collectiblesRes.message) {
        allCollectibles = collectiblesRes.message.map((item, idx) => ({
          module: "Collectible",
          "Name of Asset": item.sub_type,
          "Account Type/Titled": "Collectible",
          "Held Where": null,
          "Monetary Value": item.monetary,
          "Value as of Date": null,
          id: idx + 1,
        }));
      }

      // 3. Update dedicated PDF lists, protecting the live UI arrays
      setPdfAssetsList(allAssets);
      setPdfInventoryList(allInventory);
      setPdfCollectiblesList(allCollectibles);

      // 4. Generate the PDF
      setTimeout(async () => {
        const element = document.getElementById("pdf-report-container");
        if (!element) {
          setDownloadLoading(false);
          if (props.onDownloadLoading) props.onDownloadLoading(false);
          return;
        }

        await exportElementToPaginatedPdf({
          element,
          fileName: `Assets_Report_${moment().format("YYYYMMDD")}.pdf`,
          margin: 15,
          canvasOptions: {
            allowTaint: true,
            removeContainer: true,
          },
        });
        setDownloadLoading(false);
        if (props.onDownloadLoading) props.onDownloadLoading(false);
      }, 1000); // Wait 1 second to ensure new table lists and the base64 chart are confidently painted in DOM
    } catch (error) {
      console.error("Error generating PDF:", error);
      setDownloadLoading(false);
      if (props.onDownloadLoading) props.onDownloadLoading(false);
    }
  };

  const data = {
    labels: assetsName && assetsName,
    datasets: [
      {
        label: "Monetary Value",
        data: assetsValues && assetsValues,
        fill: false,
        backgroundColor:
          assetsValues &&
          assetsValues.map(
            (_, index) => ChartColors[index % ChartColors.length] // Cycle through ChartColors
          ),
        borderColor:
          assetsValues &&
          assetsValues.map(
            (_, index) => ChartColors[index % ChartColors.length] // Same for borderColor
          ),
      },
    ],
  };
  const getMonetaryValueAssets = async () => {
    const moduleName = "Assets";
    const data = await DASHBOARD_API.fetchMonetaryValueofModule(moduleName);
    if (
      data &&
      data.data &&
      data.data[0] &&
      data.data[0].monetary &&
      data.data[0].monetary !== null
    ) {
      return data.data[0].monetary;
    }

    return 0;
  };
  const getMonetaryValueInventory = async () => {
    const moduleName = "Property";
    const data = await DASHBOARD_API.fetchMonetaryValueofModule(moduleName);
    if (
      data &&
      data.data &&
      data.data[0] &&
      data.data[0].monetary &&
      data.data[0].monetary !== null
    ) {
      return data.data[0].monetary;
    }

    return 0;
  };
  const getMonetaryValueCollection = async () => {
    const moduleName = "Collection";
    const data = await DASHBOARD_API.fetchMonetaryValueofModule(moduleName);
    if (
      data &&
      data.data &&
      data.data[0] &&
      data.data[0].monetary &&
      data.data[0].monetary !== null
    ) {
      return data.data[0].monetary;
    }

    return 0;
  };

  const loadAssetsReportData = async () => {
    try {
      setLoading(true);
      const [
        assetsRecords,
        inventoryRecords,
        collectibleRecords,
        assetsTotal,
        collectionTotal,
        inventoryTotal,
      ] = await Promise.all([
        getAssetsList(),
        getInventoryList(),
        getCollectiblesList(),
        getMonetaryValueAssets(),
        getMonetaryValueCollection(),
        getMonetaryValueInventory(),
      ]);

      setAssetsList(assetsRecords);
      setInventoryList(inventoryRecords);
      setCollectiblesList(collectibleRecords);
      setMonetaryValue(assetsTotal || 0);
      setMonetaryValueCollection(collectionTotal || 0);
      setMonetaryValueInventory(inventoryTotal || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssetsReportData();
    const name = getFAClientName();
    if (name) setClientName(name);
  }, [props.sharedState]);

  return (
    <React.Fragment>
      <div className="reportCard1">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 className="Reportheading">Assets</h3>
        </div>
        <>
          <div className="asset-home-icon-container">
            <div className="asset-home-icon">
              <img src={assetHomeIcon} alt="" />
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: "500",
                wordBreak: "break-all",
              }}
            >
              {loading ? (
                <span className="assets-inline-value-loading">
                  <LoadingOutlined spin />
                  Loading value...
                </span>
              ) : (
                <>
                  $
                  {(+totalAssetsValue).toFixed(2).toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </>
              )}
            </div>
          </div>
          <div className="reprotcardRow">
            <div>
              <div className="reportrow">
                <div className="reportrowData">
                  <span className="reportTitle"> Client Name</span>
                  <span className="reportsubTitle">
                    {clientName || localStorage.getItem("User") || ""}
                  </span>
                </div>
                 <div className="reportrowData">
                  <span className="reportTitle"> Today's Date </span>
                  <span className="reportsubTitle">
                    {" "}
                    {moment().format("MM/DD/YYYY")}
                  </span>
                </div>
              </div>
              {/* <div className="reportrow2">
                 <div className="reportrowData">
                  <span className="reportTitle"> Plan Nickname </span>
                  <span className="reportsubTitle"> First Plan</span>
                </div> 
              </div>*/}
            </div>
          </div>
        </>
      </div>
      <div className="reportCard2" style={{ position: "relative" }}>
        <h1 className="liablitycardheading">All Assets</h1>
        <div style={{ marginTop: "1rem" }} className={loading ? "assets-section-fade" : ""}>
          {loading ? (
            <div className="assets-chart-loading">
              <Spin indicator={<LoadingOutlined spin />} />
              <span>Loading chart data...</span>
            </div>
          ) : (
            <Bar
              ref={chartRef}
              data={data}
              height={300}
              options={{
                animation: {
                  duration: downloadLoading ? 1 : 1000,
                },
                legend: {
                  display: false,
                },
                tooltips: {
                  callbacks: {
                    label: function (tooltipItem, chartData) {
                      const datasetLabel =
                        chartData.datasets[tooltipItem.datasetIndex].label || "";
                      const value = tooltipItem.yLabel;
                      const formattedValue = new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(value);
                      return `${datasetLabel}: ${formattedValue}`;
                    },
                  },
                },
                maintainAspectRatio: false,
                responsive: true,
                devicePixelRatio: 2,
                scales: {
                  xAxes: [
                    {
                      gridLines: {
                        color: "rgba(0, 0, 0, 0)",
                      },
                    },
                  ],
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true,
                        callback: function (value) {
                          return new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(value);
                        },
                      },
                    },
                  ],
                },
              }}
            />
          )}
        </div>
      </div>

      {/* Hidden PDF Report Template - rendering securely in the background */}
      <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "1000px", 
          height: "100%", 
          zIndex: -9999, 
          opacity: 0.01, 
          pointerEvents: "none", 
          overflow: "hidden" 
        }}>
        <div id="pdf-report-container">
          <AssetsPDFReport
            assetsList={pdfAssetsList.length > 0 ? pdfAssetsList : assetsList}
            inventoryList={pdfInventoryList.length > 0 ? pdfInventoryList : inventoryList}
            collectiblesList={pdfCollectiblesList.length > 0 ? pdfCollectiblesList : collectiblesList}
            totalAssets={(+totalAssetsValue).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            clientName={clientName || localStorage.getItem("User") || ""}
            chartImage={pdfChartImage}
          />
        </div>
      </div>
    </React.Fragment>
  );
});
